const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

export type ApiEnvelope<T> = {
  data: T
  meta?: Record<string, unknown>
  pagination?: Record<string, unknown>
}

type ApiErrorBody = {
  error?: {
    code?: string
    message?: string
    details?: unknown
    requestId?: string
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  readonly requestId?: string
  readonly retryAfter?: number

  constructor(status: number, code: string, message: string, options?: { details?: unknown; requestId?: string; retryAfter?: number }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = options?.details
    this.requestId = options?.requestId
    this.retryAfter = options?.retryAfter
  }
}

let accessToken: string | null = null
let refreshPromise: Promise<string | null> | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

function isApiEnvelope<T>(value: unknown): value is ApiEnvelope<T> {
  return Boolean(value && typeof value === 'object' && 'data' in value)
}

async function parseError(response: Response) {
  let body: ApiErrorBody = {}
  try {
    body = await response.json() as ApiErrorBody
  } catch {
    // Some upstream failures intentionally have no JSON body.
  }
  const requestId = response.headers.get('x-request-id') ?? body.error?.requestId
  const retryHeader = response.headers.get('retry-after')
  const fallback = response.status === 403
    ? 'You do not have permission to perform this action.'
    : response.status === 429
      ? 'Too many requests. Please wait before trying again.'
      : response.status >= 500
        ? 'Pepsa is temporarily unable to complete this request.'
        : `Request failed with status ${response.status}`

  return new ApiError(
    response.status,
    body.error?.code ?? `HTTP_${response.status}`,
    body.error?.message ?? fallback,
    {
      details: body.error?.details,
      requestId,
      retryAfter: retryHeader ? Number(retryHeader) : undefined,
    },
  )
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/v1/auth/browser/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Request-Id': crypto.randomUUID() },
    })
      .then(async (response) => {
        if (!response.ok) return null
        const payload = await response.json() as ApiEnvelope<{ accessToken: string }>
        accessToken = payload.data.accessToken
        return accessToken
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

export type ApiRequestOptions = RequestInit & {
  skipAuthRefresh?: boolean
  idempotencyKey?: string
  retryAttempt?: number
}

export async function apiClient<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { skipAuthRefresh, idempotencyKey, retryAttempt = 0, ...init } = options
  const headers = new Headers(init.headers)
  if (init.body && typeof init.body === 'string') headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  headers.set('X-Request-Id', headers.get('X-Request-Id') ?? crypto.randomUUID())
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)

  const safeToRetry = (init.method ?? 'GET').toUpperCase() === 'GET' || Boolean(idempotencyKey)
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers,
    })
  } catch (error) {
    if (safeToRetry && retryAttempt < 1 && !(error instanceof DOMException && error.name === 'AbortError'))
      return apiClient<T>(path, { ...options, retryAttempt: retryAttempt + 1 })
    throw error
  }

  if (safeToRetry && retryAttempt < 1 && [408, 425, 502, 503, 504].includes(response.status))
    return apiClient<T>(path, { ...options, retryAttempt: retryAttempt + 1 })

  if (response.status === 401 && !skipAuthRefresh && !path.startsWith('/v1/auth/browser/')) {
    const token = await refreshAccessToken()
    if (token) return apiClient<T>(path, { ...options, skipAuthRefresh: true })
    setAccessToken(null)
  }

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T
  const payload = await response.json() as ApiEnvelope<T> | T
  return isApiEnvelope<T>(payload) ? payload.data : payload
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiClient<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiClient<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body), ...options }),
  postForm: <T>(path: string, body: FormData, options?: ApiRequestOptions) =>
    apiClient<T>(path, { method: 'POST', body, ...options }),
  putForm: <T>(path: string, body: FormData, options?: ApiRequestOptions) =>
    apiClient<T>(path, { method: 'PUT', body, ...options }),
  postRaw: <T>(path: string, body: Blob, headers?: HeadersInit, options?: ApiRequestOptions) =>
    apiClient<T>(path, { method: 'POST', body, headers, ...options }),
  patch: <T>(path: string, body: unknown, options?: ApiRequestOptions) =>
    apiClient<T>(path, { method: 'PATCH', body: JSON.stringify(body), ...options }),
  delete: <T>(path: string, options?: ApiRequestOptions) => apiClient<T>(path, { method: 'DELETE', ...options }),
}

export async function downloadApiFile(path: string, filename: string) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'X-Request-Id': crypto.randomUUID(),
    },
  })
  if (!response.ok) throw await parseError(response)
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function apiErrorMessage(error: unknown) {
  if (!(error instanceof ApiError)) return 'Something went wrong. Please try again.'
  const messages: Record<string, string> = {
    INSUFFICIENT_WALLET_BALANCE: 'Your available wallet balance is too low. Fund your wallet and try again.',
    CREDIT_LIMIT_EXCEEDED: 'Your credit limit has been reached. Contact your finance administrator or Pepsa support.',
    ORDER_ALREADY_CONFIRMED: 'This delivery has already been confirmed. Its latest status is being refreshed.',
    OPTIMISTIC_LOCK_CONFLICT: 'This record changed while you were working. Refresh and review the latest version.',
    MASTER_UNCONFIGURED: 'Delivery pricing is temporarily unavailable. Please contact support with the request ID.',
  }
  const message = messages[error.code] ?? error.message
  return error.requestId ? `${message} Request ID: ${error.requestId}` : message
}
